import { useEffect, useRef, useCallback } from 'react';
import { useAuth, useClerk } from '../lib/auth';
import { useAppSession } from './useAppSession';
import { authApi } from '../lib/api/auth';
import { apiBaseUrl } from '../lib/apiBaseUrl';
import { capture } from '../lib/analytics';
import { log } from '../lib/logger';
import { pendingOpenBelongsToSession, readPendingExtensionOpen } from '../lib/pendingExtensionOpen';
import { acknowledgeExtensionAuth, EXTENSION_AUTH_FAILED_EVENT, EXTENSION_AUTH_REQUEST_EVENT } from '../lib/extensionAuthAcknowledgment';

const DEDUPE_WINDOW_MS = 5 * 60 * 1000;
const SESSION_STORAGE_KEY = 'oqa_last_extension_auth_at';
// Covers Clerk's token read, session minting, and the extension acknowledgment.
// The acknowledgment's own timer cannot bound a token read that never settles.
const PENDING_AUTH_DEADLINE_MS = 20_000;

export interface BridgeDedupeEntry {
  teamId: string;
  at: number;
  userId?: string;
  recoveryTeam?: boolean;
}

export function shouldDedupeBridgePush(
  prior: BridgeDedupeEntry | null,
  current: { teamId: string; now: number },
): boolean {
  if (!prior) return false;
  if (prior.teamId !== current.teamId) return false;
  return current.now - prior.at < DEDUPE_WINDOW_MS;
}

/**
 * The bridge should re-arm (re-push auth) when the dashboard's active team
 * changes. `lastBridgedTeam` is null before the first bridge — that is not a
 * change, so no re-arm.
 */
export function shouldRearmBridge(
  lastBridgedTeam: string | null,
  currentTeam: string,
): boolean {
  return lastBridgedTeam !== null && lastBridgedTeam !== currentTeam;
}

export function readBridgeDedupeEntry(): BridgeDedupeEntry | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BridgeDedupeEntry;
    if (typeof parsed.teamId !== 'string' || typeof parsed.at !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeBridgeDedupeEntry(entry: BridgeDedupeEntry): void {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // best-effort
  }
}

export function clearBridgeDedupe(): void {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

// Cross-tab in-flight claim. The sessionStorage dedupe above is PER-TAB, so it
// cannot stop a SECOND dashboard tab from independently pushing auth. A quiet
// session check can overlap an already-open dashboard or onboarding tab.
// Without a shared claim, both can mint a token and re-hydrate the extension
// repeatedly (SSE reconnect + re-identify per push). This claim lives in localStorage
// (visible across same-origin tabs) and is written SYNCHRONOUSLY before the async
// token mint, so a tab that probes milliseconds later sees the claim and skips.
// Incident: investigate 2026-07-06 (emily.veras@15five.com signup — 2 tabs,
// auth_completed x3, auto_auth_completed x2, $identify x5 in ~2.4s).
// A token-less extension (reinstall / storage wipe) still recovers: it posts a
// pull_request, which bypasses both dedupes.
export const BRIDGE_CLAIM_KEY = 'oqa_bridge_claim';

export function readBridgeClaim(): BridgeDedupeEntry | null {
  try {
    const raw = window.localStorage.getItem(BRIDGE_CLAIM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BridgeDedupeEntry;
    if (typeof parsed.teamId !== 'string' || typeof parsed.at !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeBridgeClaim(entry: BridgeDedupeEntry): void {
  try {
    window.localStorage.setItem(BRIDGE_CLAIM_KEY, JSON.stringify(entry));
  } catch {
    // best-effort
  }
}

export function releaseBridgeClaim(): void {
  try {
    window.localStorage.removeItem(BRIDGE_CLAIM_KEY);
  } catch {
    // best-effort
  }
}

/**
 * Atomic check-and-claim for the cross-tab bridge race. Returns 'skip' when a
 * concurrent (or recent, within the same 5-minute window) claim for the SAME
 * team already exists — the caller must NOT push. Otherwise writes the claim
 * synchronously (so a tab probing milliseconds later dedupes) and returns
 * 'proceed'. A team switch or a stale claim always proceeds.
 */
export function claimBridgeOrSkip(teamId: string, now: number): 'proceed' | 'skip' {
  if (shouldDedupeBridgePush(readBridgeClaim(), { teamId, now })) {
    return 'skip';
  }
  writeBridgeClaim({ teamId, at: now });
  return 'proceed';
}

/**
 * Clerk session statuses that mean the session ended on its OWN — a passive
 * expiry or idle timeout — rather than a deliberate sign-out. A passive end must
 * NOT tear down the extension's INDEPENDENT 30-day session.
 * Incident: investigate 2026-06-25 — the dashboard sign-out sync fired on every
 * Clerk isSignedIn true->false (incl. passive expiry), wiping valid extension
 * sessions and stranding returning users on the sidebar sign-in card (9 users in
 * 120d, 4 of them repeatedly).
 */
const PASSIVE_CLERK_SESSION_STATUSES = new Set(['expired', 'abandoned']);

export type ClerkSignOutKind = 'deliberate' | 'passive';

/**
 * Read the status of the session that just ended from the Clerk client. Clerk
 * keeps the terminal session in `client.sessions` with its end status (e.g.
 * 'expired'/'abandoned' for a passive end, 'ended'/'removed' for a sign-out).
 * Returns the first non-active status found, or null when none is identifiable.
 */
export function readEndedSessionStatus(
  client: { sessions?: Array<{ status?: string | null } | null> | null } | null | undefined,
): string | null {
  const sessions = client?.sessions ?? [];
  for (const session of sessions) {
    const status = session?.status;
    if (status && status !== 'active') return status;
  }
  return null;
}

/**
 * Classify a Clerk isSignedIn true->false transition. Only a `deliberate`
 * sign-out should sync to (and wipe) the extension. An unknown/absent status is
 * treated as `deliberate` so a real sign-out is never missed on a shared machine
 * (security-safe default): the fix can only SKIP a sync when it positively
 * identifies a passive end, never silently keep a deliberate sign-out alive.
 */
export function classifyClerkSignOut(endedSessionStatus: string | null | undefined): ClerkSignOutKind {
  if (endedSessionStatus && PASSIVE_CLERK_SESSION_STATUSES.has(endedSessionStatus)) {
    return 'passive';
  }
  return 'deliberate';
}

/**
 * Two-way auth bridge between dashboard and extension.
 *
 * PUSH (primary): When the dashboard detects the extension DOM host element,
 * it proactively sends auth credentials. This fires when the user installs the
 * extension while the dashboard is open, or navigates to the dashboard with the
 * extension already present.
 *
 * PULL (fallback): If the extension content script loads before the dashboard
 * React app mounts, the push probe misses it. The extension posts
 * OVERLAYQA_AUTH_REQUEST and the dashboard responds here. Queued requests are
 * replayed once session data becomes available.
 *
 * SIGN-OUT SYNC: Monitors Clerk isSignedIn transitions. When the user signs
 * out from the dashboard, posts OVERLAYQA_DASHBOARD_SIGN_OUT so the extension
 * content script can forward to the background worker and clear extension auth.
 * Reverse direction: listens for OVERLAYQA_EXTENSION_SIGN_OUT from the
 * extension content script and calls clerk.signOut().
 */
export function useExtensionAuthBridge() {
  const { getToken, isSignedIn } = useAuth();
  const { data: session } = useAppSession();
  const { signOut: clerkSignOut, client } = useClerk();
  const bridgedRef = useRef(false);
  const pendingRequestRef = useRef(false);
  const wasSignedInRef = useRef<boolean | null>(null);
  const handlingExtSignOutRef = useRef(false);
  const lastBridgedTeamRef = useRef<string | null>(null);
  const lastBridgedUserRef = useRef<string | null>(null);
  const currentSessionRef = useRef(session);
  currentSessionRef.current = session;
  const attemptRef = useRef(0);
  const inFlightRef = useRef<string | null>(null);

  const pushAuth = useCallback(async (source: string) => {
    const explicitRecovery = source === 'pending_open';
    if (bridgedRef.current && !explicitRecovery) return;
    if (!session) {
      // Session not ready yet. If this was a pull request, queue it.
      if (source === 'pull_request') {
        pendingRequestRef.current = true;
      }
      return;
    }

    const pending = readPendingExtensionOpen();
    const recovery = pendingOpenBelongsToSession(pending, session) ? pending : null;
    const requiresAcknowledgment = Boolean(recovery && (recovery.source === 'team_review' || recovery.teamId !== session.activeTeamId));
    const prior = readBridgeDedupeEntry();
    // Keep this tab's explicitly selected recovery workspace after its receipt
    // clears the pending intent. Revalidate membership on every auth renewal.
    const priorRecoveryTeam = prior?.recoveryTeam && prior.userId === session.user.id
      && (prior.teamId === session.activeTeamId || session.teams?.some(team => team.id === prior.teamId)) ? prior.teamId : null;
    const teamId = recovery?.teamId ?? priorRecoveryTeam ?? session.activeTeamId;
    if (!teamId) return;
    const attemptKey = `${session.user.id}:${teamId}:${recovery?.handoffId ?? ''}`;
    if (inFlightRef.current === attemptKey) return;
    const attempt = ++attemptRef.current;
    inFlightRef.current = attemptKey;
    let expired = false;
    const stillCurrent = () => {
      const current = currentSessionRef.current;
      return !expired && attemptRef.current === attempt && current?.user.id === session.user.id
        && (current.activeTeamId === teamId || current.teams?.some(team => team.id === teamId));
    };

    // Dedupe across App remounts within 5 minutes for the SAME teamId.
    // Team switches still re-mint because the teamId differs.
    //
    // EXCEPTION: a pull request from the extension is positive evidence that
    // chrome.storage.local.userId is empty (the content script's
    // requestDashboardAuth only fires when userId is absent). If our dedupe
    // claims we bridged but the extension says it has no token, the dedupe
    // is stale — typically because extension state was wiped (Z dev reset,
    // manual chrome://extensions storage clear, reinstall, or a prior push
    // whose chrome.storage write failed silently). Bypass dedupe and re-mint.
    const isPullRequest = source === 'pull_request' || source === 'pull_request_replay' || explicitRecovery || requiresAcknowledgment;
    if (isPullRequest) {
      clearBridgeDedupe();
      releaseBridgeClaim();
    } else {
      const prior = readBridgeDedupeEntry();
      if (shouldDedupeBridgePush(prior, { teamId, now: Date.now() })) {
        log.info('[ExtensionAuthBridge] Skipping push, within dedupe window', { source, teamId });
        bridgedRef.current = true;
        inFlightRef.current = null;
        return;
      }
      // A quiet session check can overlap another dashboard tab. The per-tab
      // sessionStorage dedupe cannot stop the second tab from pushing. Claim synchronously here, BEFORE
      // the async mint below, so a tab probing milliseconds later dedupes and the
      // extension is not re-authed 2-3x (the sidebar glitch). Incident:
      // investigate 2026-07-06.
      if (claimBridgeOrSkip(teamId, Date.now()) === 'skip') {
        log.info('[ExtensionAuthBridge] Skipping push, another tab is bridging', { source, teamId });
        bridgedRef.current = true;
        inFlightRef.current = null;
        return;
      }
    }

    bridgedRef.current = true;
    const deadline = recovery ? window.setTimeout(() => {
      if (!stillCurrent()) return;
      expired = true;
      // Release the pending operation for a deliberate retry, while leaving
      // automatic probes settled. Late token/mint results fail stillCurrent.
      inFlightRef.current = null;
      window.dispatchEvent(new CustomEvent(EXTENSION_AUTH_FAILED_EVENT, { detail: {
        userId: session.user.id, handoffId: recovery.handoffId,
        message: 'Connecting to the extension took too long. Try again.',
      } }));
    }, PENDING_AUTH_DEADLINE_MS) : null;

    try {
      const token = await getToken();
      if (!stillCurrent()) return;
      if (!token) {
        if (recovery) throw new Error('Sign in again to open this project in the extension.');
        bridgedRef.current = false;
        if (!isPullRequest) releaseBridgeClaim();
        return;
      }

      log.info('[ExtensionAuthBridge] Issuing session token', { source });
      const response = await authApi.getExtensionSessionToken(token, { teamId });
      if (!stillCurrent()) return;

      if (response) {
        if (response.userId !== session.user.id || response.teamId !== teamId) throw new Error('The extension account changed. Try again.');
        if (requiresAcknowledgment) {
          await acknowledgeExtensionAuth(response);
          if (!stillCurrent()) return;
        } else window.postMessage({
          type: 'OVERLAYQA_DASHBOARD_AUTH',
          // Which api minted this token. The extension refuses the handoff
          // unless it matches its own compiled api origin, so a dashboard on
          // one environment can no longer authenticate an extension on
          // another (2026-08-28 incident; @overlayqa/shared authBridgeOrigin).
          apiOrigin: apiBaseUrl(),
          payload: response,
        }, window.location.origin);

        writeBridgeDedupeEntry({ teamId, at: Date.now(), ...(recovery || priorRecoveryTeam ? { userId: session.user.id, recoveryTeam: true } : {}) });

        // Signal usePendingExtensionOpen that auth is ready so it can
        // safely fire the OVERLAYQA_OPEN_SITE message.
        window.dispatchEvent(new CustomEvent('overlayqa-auth-bridged', { detail: { userId: session.user.id, teamId } }));

        // Tag source with /get-started entry-point info for funnel analysis.
        const inferredSource =
          source === 'dom_detection' && window.location.pathname.startsWith('/get-started')
            ? 'get_started_route'
            : source;

        log.info('[ExtensionAuthBridge] Posted auto-auth to extension');
        capture('extension_auto_auth_bridged', { source: inferredSource });
        // Legacy event name still consumed by existing PostHog funnels.
        // Do not remove without updating the funnels.
        capture('auto_auth_initiated', { source: inferredSource });
      }
    } catch (err) {
      if (!stillCurrent()) return;
      const msg = err instanceof Error ? err.message : '';
      // 409 = BOOTSTRAP_REQUIRED: user row doesn't exist yet in the DB.
      // The bootstrap call (useAppSession) will create it. Stop retrying
      // to avoid hammering the server; the next poll after bootstrap
      // completes will succeed because the auth cache will be invalidated.
      if (msg.includes('bootstrap') || msg.includes('Bootstrap')) {
        log.info('[ExtensionAuthBridge] Bootstrap not yet complete, will retry after session loads');
        bridgedRef.current = false;
        pendingRequestRef.current = true; // replay once session refreshes
        if (!isPullRequest) releaseBridgeClaim();
        return;
      }
      log.warn('[ExtensionAuthBridge] Failed to bridge auth', { err });
      // A failed pending handoff offers Try again. Keep automatic probes
      // settled so an older extension cannot cause endless token minting;
      // explicit pending_open requests still bypass this latch.
      bridgedRef.current = Boolean(recovery);
      if (!isPullRequest) releaseBridgeClaim();
      if (recovery) window.dispatchEvent(new CustomEvent(EXTENSION_AUTH_FAILED_EVENT, { detail: { userId: session.user.id, handoffId: recovery.handoffId, message: err instanceof Error ? err.message : 'The extension could not connect. Try again.' } }));
    } finally {
      if (deadline !== null) window.clearTimeout(deadline);
      if (attemptRef.current === attempt) inFlightRef.current = null;
    }
  }, [session, getToken]);

  useEffect(() => {
    const request = () => { if (pendingOpenBelongsToSession(readPendingExtensionOpen(), session)) void pushAuth('pending_open'); };
    window.addEventListener(EXTENSION_AUTH_REQUEST_EVENT, request);
    return () => window.removeEventListener(EXTENSION_AUTH_REQUEST_EVENT, request);
  }, [session, pushAuth]);

  // Re-arm the bridge when the dashboard's active team changes. bridgedRef is a
  // one-shot latch and is NOT team-aware: switching account or team via SPA
  // navigation (no full reload) leaves it set, so the extension keeps the prior
  // team's session and every capture 403s "Not a member of this team".
  // pushAuth's own dedupe stays team-scoped, so a redundant re-push for the
  // same team is still skipped.
  useEffect(() => {
    const teamId = session?.activeTeamId ?? null;
    if (!teamId) return;
    if (shouldRearmBridge(lastBridgedTeamRef.current, teamId)
      || (lastBridgedUserRef.current !== null && lastBridgedUserRef.current !== session?.user.id)) {
      log.info('[ExtensionAuthBridge] Active team changed, re-arming bridge', {
        from: lastBridgedTeamRef.current,
        to: teamId,
      });
      bridgedRef.current = false;
      ++attemptRef.current;
      inFlightRef.current = null;
    }
    lastBridgedTeamRef.current = teamId;
    lastBridgedUserRef.current = session?.user.id ?? null;
  }, [session]);

  // PUSH: poll for extension DOM host element, push auth when detected
  useEffect(() => {
    if (bridgedRef.current || !session) return;

    const OVERLAY_HOST_ID = 'overlayqa-overlay-host';

    function probe() {
      if (bridgedRef.current || pendingRequestRef.current) return;
      if (document.getElementById(OVERLAY_HOST_ID)) {
        log.info('[ExtensionAuthBridge] Extension detected via DOM probe');
        pushAuth('dom_detection');
      }
    }

    // Probe immediately, then poll
    probe();
    const interval = setInterval(probe, 3000);

    return () => clearInterval(interval);
  }, [session, pushAuth]);

  // PULL: listen for extension auth requests
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== window) return;
      if (event.data?.type !== 'OVERLAYQA_AUTH_REQUEST') return;
      log.info('[ExtensionAuthBridge] Received auth request from extension');
      pushAuth('pull_request');
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [pushAuth]);

  // Replay queued pull requests once session loads
  useEffect(() => {
    if (session && pendingRequestRef.current && !bridgedRef.current) {
      pendingRequestRef.current = false;
      log.info('[ExtensionAuthBridge] Replaying queued auth request');
      pushAuth('pull_request_replay');
    }
  }, [session, pushAuth]);

  // SIGN-OUT SYNC: Dashboard -> Extension
  // When Clerk isSignedIn transitions from true to false, notify the extension.
  useEffect(() => {
    if (isSignedIn === undefined) return;

    const prev = wasSignedInRef.current;
    wasSignedInRef.current = isSignedIn ?? false;

    // Only act on a true -> false transition (actual end, not initial load)
    if (prev === true && !isSignedIn && !handlingExtSignOutRef.current) {
      // Allow re-bridging on the next sign-in regardless of WHY the session
      // ended (clear the one-shot latch + dedupe + cross-tab claim so a
      // re-sign-in re-bridges).
      bridgedRef.current = false;
      ++attemptRef.current;
      inFlightRef.current = null;
      clearBridgeDedupe();
      releaseBridgeClaim();

      // A passive Clerk session end (expiry / idle timeout) must NOT wipe the
      // extension's independent 30-day session — only a deliberate sign-out
      // syncs. Unknown status defaults to deliberate so a real sign-out is never
      // missed (security-safe). Incident: investigate 2026-06-25.
      const kind = classifyClerkSignOut(readEndedSessionStatus(client));
      if (kind === 'passive') {
        log.info('[ExtensionAuthBridge] Clerk session ended passively; not syncing sign-out to extension');
        capture('extension_sign_out_skipped', { reason: 'passive_session_end' });
        return;
      }

      log.info('[ExtensionAuthBridge] Deliberate sign-out, notifying extension');
      window.postMessage(
        // Same origin claim as the auth message above. Without it a dev
        // dashboard on localhost can clear a PRODUCTION extension session,
        // which is the half of the 2026-08-28 incident that surfaced as
        // "it keeps signing me out of that account".
        { type: 'OVERLAYQA_DASHBOARD_SIGN_OUT', apiOrigin: apiBaseUrl() },
        window.location.origin,
      );
      capture('extension_sign_out_synced', { direction: 'dashboard_to_extension' });
    }
    // `client` is read only at the isSignedIn transition; intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  // SIGN-OUT SYNC: Extension -> Dashboard
  // When the extension content script posts OVERLAYQA_EXTENSION_SIGN_OUT,
  // sign out of Clerk so both surfaces stay in sync.
  useEffect(() => {
    function onExtensionSignOut(event: MessageEvent) {
      if (event.source !== window) return;
      if (event.data?.type !== 'OVERLAYQA_EXTENSION_SIGN_OUT') return;

      log.info('[ExtensionAuthBridge] Extension signed out, signing out of Clerk');
      handlingExtSignOutRef.current = true;
      capture('extension_sign_out_synced', { direction: 'extension_to_dashboard' });
      clerkSignOut({ redirectUrl: '/sign-in?signedOut=1' }).finally(() => {
        handlingExtSignOutRef.current = false;
      });
    }

    window.addEventListener('message', onExtensionSignOut);
    return () => window.removeEventListener('message', onExtensionSignOut);
  }, [clerkSignOut]);
}
